import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'components/styles/import-record.css';
import ImportStatus from './import-dialog';
import SearchRecords from './search-records';
import EmployeeRecord from './employee-record';
import DelinkDeleteSuccessMessage from './delink-delete-success-message';
import Cookies from 'universal-cookie';
import { MAX_FILE_SIZE } from 'constants';
import { PARTNERSHIP_URL } from '../constants/index';
import { ReactComponent as DownloadIcon } from 'assets/images/download.svg';

const ImportRecords = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [closeDialog, setCloseDialog] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [employeeData, setEmployeeData] = useState(null);
  const [importError, setImportError] = useState('');
  const [showImportStatus, setShowImportStatus] = useState(false);
  const [showDelinkSuccessMessage, setShowDelinkSuccessMessage] = useState(false);
  const [showDeleteSuccessMessage, setShowDeleteSuccessMessage] = useState(false);

  const cookies = new Cookies();

  useEffect(() => {
    if (showDelinkSuccessMessage || showDeleteSuccessMessage) {
      setEmployeeData(null);
    }
  }, [showDelinkSuccessMessage, showDeleteSuccessMessage]);

  const handleFileChange = async (event) => {
    const { files } = event.target;
    const file = files[0];

    if (file && file.size === 0) {
      setImportError('The file uploaded does not have any records. Please check and upload again.');
      setSelectedFile(file);
      setShowImportStatus(true);
      setImportStatus('');
    } else if (file && file?.size <= MAX_FILE_SIZE) {
      setImportStatus('importing');
      setSelectedFile(file);
      setShowImportStatus(true);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const token = cookies.get('token');

        const response = await axios.post(`${PARTNERSHIP_URL}/employees/upload-file`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        const inValidCount = response.data.data.invalidRowsCount;
        const validCount = response.data.data.totalCount - response.data.data.invalidRowsCount;

        if (response.status === 201) {
          if (inValidCount) {
            setImportStatus('');
            setImportError(
              `This file contains ${validCount} valid record(s) processed successfully. However, ${inValidCount} record(s) are corrupt or invalid and were not processed.`
            );
          } else {
            setImportStatus('success');
            const timeout = setTimeout(() => {
              setShowImportStatus(false);
            }, 5000);
            return () => clearTimeout(timeout);
          }
        }
      } catch (error) {
        setImportStatus('unsuccesful');
      }
    } else {
      setSelectedFile('Corrupt File');
      setImportError('File size should be less than 5MB');
      setShowImportStatus(true);
      event.target.value = null;
    }
  };

  const handleInputButtonClick = () => {
    document.getElementById('file-input').click();
    setSelectedFile('');
  };

  const downloadSampleFile = () => {
    // Define the CSV content
    const csvContent = 'employeeCode,action\nemp123,add\nemp234,delete';

    // Create a Blob from the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv' });

    // Create a link element and trigger the download
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = 'sample.csv';
    document.body.appendChild(downloadLink); // Append the link to the body (required for Firefox)
    downloadLink.click();
    document.body.removeChild(downloadLink); // Remove the link after download
  };

  useEffect(() => {
    let timer;
    if (showDelinkSuccessMessage || showDeleteSuccessMessage) {
      timer = setTimeout(() => {
        setShowDelinkSuccessMessage(false);
        setShowDeleteSuccessMessage(false);
      }, 4000);
    }
    return () => clearTimeout(timer);
  }, [showDelinkSuccessMessage, showDeleteSuccessMessage]);

  return (
    <div className="import-container">
      <div className="import">
        <div className="import-box">
          <div className="download-icon">
            <DownloadIcon/>
          </div>
          <div className="import-content">
            <div className="import-employees">Import employees records</div>
            <div className="import-format">
              You can import records of employee details in .CSV format, up to 5MB.
            </div>
            <div className="import-controls">
              <input
                type="file"
                id="file-input"
                style={{ display: 'none' }}
                onClick={(event) => {
                  setImportError('');
                  event.target.value = null;
                }}
                onChange={(event) => {
                  handleFileChange(event);
                  setCloseDialog(false);
                }}
                accept=".csv"
                multiple
              />
              <button className="import-button" onClick={handleInputButtonClick}>
                Import
              </button>
              <div className="download-sample-file" onClick={downloadSampleFile}>
                Download sample file
              </div>
            </div>
          </div>
        </div>

        <div className="search-container">
          <SearchRecords
            employeeData={employeeData}
            setEmployeeData={setEmployeeData}
            showDeleteSuccessMessage={showDeleteSuccessMessage}
            showDelinkSuccessMessage={showDelinkSuccessMessage}
          />
          {showDelinkSuccessMessage && (
            <DelinkDeleteSuccessMessage setShowDelinkSuccessMessage={setShowDelinkSuccessMessage} />
          )}
          {showDeleteSuccessMessage && (
            <DelinkDeleteSuccessMessage
              setShowDeleteSuccessMessage={setShowDeleteSuccessMessage}
              deleteMessage
            />
          )}
        </div>
      </div>

      {employeeData ? (
        <EmployeeRecord
          employeeData={employeeData}
          setShowDelinkSuccessMessage={setShowDelinkSuccessMessage}
          setShowDeleteSuccessMessage={setShowDeleteSuccessMessage}
        />
      ) : (
        <div></div>
      )}

      {selectedFile && (
        <ImportStatus
          selectedFile={selectedFile.name}
          closeDialog={closeDialog}
          setCloseDialog={setCloseDialog}
          importError={importError}
          setImportError={setImportError}
          handleInputButtonClick={handleInputButtonClick}
          showImportStatus={showImportStatus}
          importStatus={importStatus}
        />
      )}
    </div>
  );
};

export default ImportRecords;
